import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Button, Input, Modal, Text } from '@teals/ui-web';
import type { OrganisationMembership } from '../../store/api/portalApi';
import {
  useAssumeOrgAdminMutation,
  useEndOrgAssumptionMutation,
  useSearchStaffOrganisationsQuery,
} from '../../store/api/staffApi';

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-top: 0.75rem;
`;

const Row = styled.div<{ $current?: boolean }>`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem;
  border: 1px solid ${({ $current }) => ($current ? 'var(--accent)' : 'var(--border)')};
  border-radius: 10px;
  background: ${({ $current }) => ($current ? 'var(--accent-subtle)' : 'var(--bg-surface)')};
`;

const RowMain = styled.button`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.125rem;
  flex: 1;
  min-width: 0;
  background: transparent;
  border: none;
  padding: 0;
  text-align: left;
  cursor: pointer;
  color: var(--text-primary);

  &:hover {
    color: var(--accent);
  }

  &:focus-visible {
    outline: 2px solid var(--accent);
    outline-offset: 2px;
  }
`;

const Meta = styled.span`
  font-size: 0.8125rem;
  color: var(--text-secondary);
`;

const SectionLabel = styled.p`
  margin: 1.25rem 0 0;
  font-size: 0.8125rem;
  font-weight: 600;
  color: var(--text-secondary);
`;

const Hint = styled.p`
  margin: 0.5rem 0 0;
  font-size: 0.8125rem;
  color: var(--text-secondary);
`;

const ErrorText = styled.p`
  margin: 0.75rem 0 0;
  font-size: 0.8125rem;
  color: var(--error);
`;

function formatExpiry(iso?: string): string {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

export interface OrgSwitcherDialogProps {
  open: boolean;
  onClose: () => void;
  organisations: OrganisationMembership[];
  activeOrgId?: string;
  isStaff: boolean;
  onSelect: (orgId: string) => Promise<void> | void;
  onCleared: () => void;
}

const OrgSwitcherDialog: React.FC<OrgSwitcherDialogProps> = ({
  open,
  onClose,
  organisations,
  activeOrgId,
  isStaff,
  onSelect,
  onCleared,
}) => {
  const [rawSearch, setRawSearch] = useState('');
  const [search, setSearch] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [assumeOrgAdmin, { isLoading: assuming }] = useAssumeOrgAdminMutation();
  const [endOrgAssumption, { isLoading: ending }] = useEndOrgAssumptionMutation();

  useEffect(() => {
    const timer = window.setTimeout(() => setSearch(rawSearch.trim()), 300);
    return () => window.clearTimeout(timer);
  }, [rawSearch]);

  useEffect(() => {
    if (!open) {
      setRawSearch('');
      setSearch('');
      setError(null);
    }
  }, [open]);

  const memberships = organisations.filter((org) => !org.assumed);
  const assumptions = organisations.filter((org) => org.assumed);
  const taken = new Set(organisations.map((org) => org.id));
  const canSearch = isStaff && search.length >= 2;
  const searchQuery = useSearchStaffOrganisationsQuery(
    { q: search },
    { skip: !open || !canSearch },
  );
  const results = (searchQuery.data?.items || []).filter(
    (org) => org.orgId && !taken.has(org.orgId),
  );

  const selectOrg = async (orgId: string) => {
    setError(null);
    if (orgId === activeOrgId) {
      onClose();
      return;
    }
    try {
      await onSelect(orgId);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not switch organisation.');
    }
  };

  const handleAssume = async (orgId: string) => {
    setError(null);
    try {
      await assumeOrgAdmin(orgId).unwrap();
      await onSelect(orgId);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not switch organisation.');
    }
  };

  const handleRemove = async (orgId: string) => {
    setError(null);
    try {
      await endOrgAssumption(orgId).unwrap();
      if (orgId !== activeOrgId) return;
      const fallback = memberships.find((org) => org.id !== orgId);
      if (fallback) {
        await onSelect(fallback.id);
      } else {
        onCleared();
      }
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not end the support session.');
    }
  };

  return (
    <Modal isOpen={open} onClose={onClose} title="Switch organisation">
      <Text variant="body2">
        Choose an organisation. Switching reloads the portal in that context.
      </Text>
      <List>
        {memberships.map((org) => {
          const current = org.id === activeOrgId;
          return (
            <Row key={org.id} $current={current}>
              <RowMain type="button" onClick={() => void selectOrg(org.id)}>
                <strong>{org.name}</strong>
                <Meta>{org.roleName || 'Member'}</Meta>
              </RowMain>
              {current ? <Meta>Current</Meta> : null}
            </Row>
          );
        })}
      </List>

      {isStaff && assumptions.length > 0 && (
        <>
          <SectionLabel>Assumed organisations</SectionLabel>
          <List>
            {assumptions.map((org) => {
              const current = org.id === activeOrgId;
              const until = formatExpiry(org.expiresAt);
              return (
                <Row key={org.id} $current={current}>
                  <RowMain type="button" onClick={() => void selectOrg(org.id)}>
                    <strong>{org.name}</strong>
                    <Meta>
                      admin · support session{until ? ` · until ${until}` : ''}
                    </Meta>
                  </RowMain>
                  <Button
                    variant="secondary"
                    size="sm"
                    disabled={ending}
                    onClick={() => void handleRemove(org.id)}
                  >
                    Remove
                  </Button>
                </Row>
              );
            })}
          </List>
        </>
      )}

      {isStaff && (
        <>
          <SectionLabel>Support another organisation</SectionLabel>
          <div style={{ marginTop: '0.5rem' }}>
            <Input
              value={rawSearch}
              onChange={(event) => setRawSearch(event.target.value)}
              placeholder="Search organisations"
              aria-label="Search organisations"
              fullWidth
            />
          </div>
          {rawSearch.trim().length > 0 && rawSearch.trim().length < 2 && (
            <Hint>Type at least 2 characters.</Hint>
          )}
          {canSearch && (
            <List>
              {searchQuery.isFetching && results.length === 0 ? (
                <Hint>Searching…</Hint>
              ) : null}
              {!searchQuery.isFetching && results.length === 0 ? (
                <Hint>No organisations match.</Hint>
              ) : null}
              {results.map((org) => (
                <Row key={org.orgId}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <strong>{org.name}</strong>
                    <Meta style={{ display: 'block' }}>
                      {org.businessType || org.status}
                    </Meta>
                  </div>
                  <Button
                    variant="primary"
                    size="sm"
                    disabled={assuming}
                    onClick={() => void handleAssume(org.orgId)}
                  >
                    Switch
                  </Button>
                </Row>
              ))}
            </List>
          )}
        </>
      )}
      {error ? <ErrorText>{error}</ErrorText> : null}
    </Modal>
  );
};

export default OrgSwitcherDialog;
